<template>
  <editor ref="editor" :editable="mode === 'edit'" :extensions="extensions" @update="onUpdate" class="bg-white flex flex-col w-full relative">
    <div v-if="ready && mode === 'edit'" slot="menubar" slot-scope="{ nodes, marks, focus }" class="bg-grey-lighter border sticky pin-t">
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light relative group" title="Style">
        <i class="fas fa-heading fa-fw"></i>
        <i class="fas fa-caret-down"></i>
        <div class="bg-white absolute pin-l pin-t-full border rounded-b whitespace-no-wrap shadow hidden group-hover:block z-10">
          <div class="flex flex-col">
            <button class="border-b px-3 py-2 hover:bg-grey-lighter text-left" :class="{'font-bold': nodes.paragraph.active()}" @click="nodes.paragraph.command">
              Paragraph
            </button>
            <button v-for="i in 3" :key="i" class="border-b px-3 py-2 hover:bg-grey-lighter text-left" :class="{'font-bold': nodes.heading.active({ level: i })}" @click="nodes.heading.command({ level: i })">
              Heading {{ i }}
            </button>
          </div>
        </div>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light border-l" title="Bold" @click="marks.bold.command">
        <i class="fas fa-bold fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Italics" @click="marks.italic.command">
        <i class="fas fa-italic fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Underline" @click="marks.underline.command">
        <i class="fas fa-underline fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light border-l" title="Align Left" @click="content.align = 'text-left'">
        <i class="fas fa-align-left fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Align Center" @click="content.align = 'text-center'">
        <i class="fas fa-align-center fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Justify" @click="content.align = 'text-justify'">
        <i class="fas fa-align-justify fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Align Right" @click="content.align = 'text-right'">
        <i class="fas fa-align-right fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light border-l" title="Ordered List" @click="nodes.ordered_list.command">
        <i class="fas fa-list-ol fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Unordered List" @click="nodes.bullet_list.command">
        <i class="fas fa-list-ul fa-fw"></i>
      </button>
      <!-- <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light border-l" title="Background Colour">
        <i class="fas fa-fill-drip fa-fw"></i>
        <i class="fas fa-caret-down"></i>
        <div class="bg-white absolute pin-r pin-t-full border rounded-b whitespace-no-wrap shadow hidden group-hover:block z-10">
          Test
        </div>
      </button> -->
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Insert Hyperlink" @click="setLink(marks.link.command, marks.link.attrs.href)">
        <i class="fas fa-link fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" v-if="marks.link.active()" title="Remove Hyperlink" @click="marks.link.command">
        <i class="fas fa-unlink fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light relative group" title="Columns">
        <i class="fas fa-bars fa-fw fa-rotate-90"></i>
        <i class="fas fa-caret-down"></i>
        <div class="bg-white absolute pin-r pin-t-full border rounded-b whitespace-no-wrap shadow hidden group-hover:block z-10">
          <div class="flex flex-col">
            <button v-for="i in 3" :key="i" class="border-b px-3 py-2 hover:bg-grey-lighter text-left" :class="{'font-bold': content.columns === i}" @click="content.columns = i">
              {{ i }} Column{{ i > 1 ? 's' : '' }}
            </button>
          </div>
        </div>
      </button>
    </div>
    <div slot="content" slot-scope="props" :class="[columns, content.align, 'container p-3 mx-auto ezine-editor']"></div>
  </editor>
</template>

<script>
import { Editor } from 'tiptap'
import {
  HardBreakNode,
  LinkMark,
  BoldMark,
  ItalicMark,
  UnderlineMark,
  ImageNode,
  HeadingNode,
  ListItemNode,
  BulletListNode,
  OrderedListNode,
  HistoryExtension,
  PlaceholderExtension
} from 'tiptap-extensions'

export default {
  components: { Editor },
  props: {
    value: Object,
    mode: String
  },
  data () {
    return {
      ready: false,
      selection: null,
      content: this.value,
      extensions: [
        new HardBreakNode(),
        new LinkMark(),
        new BoldMark(),
        new ItalicMark(),
        new UnderlineMark(),
        new ImageNode(),
        new HeadingNode({ maxLevel: 3 }),
        new ListItemNode(),
        new BulletListNode(),
        new OrderedListNode(),
        new HistoryExtension(),
        new PlaceholderExtension()
      ]
    }
  },
  mounted () {
    this.$refs.editor.setContent(this.content.body)
    this.ready = true
  },
  computed: {
    columns () {
      return `md:columns-${this.content.columns}`
    }
  },
  methods: {
    setLink (command, previousUrl) {
      const url = window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.unsetLink(command)
      } else {
        command({ href: url, target: '_blank' })
      }

      this.$refs.editor.focus()
    },
    unsetLink (command) {
      command({ href: null, target: '_blank' })
    },
    onUpdate ({ getHTML }) {
      let html = getHTML()
      if (/^<p>\s*<\/p>$/i.test(html)) {
        html = ''
      }

      this.content.body = html
    }
  },
  watch: {
    content: {
      handler () {
        this.$emit('input', this.content)
      },
      deep: true
    }
  }
}
</script>
